import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLNAuth } from '@shared/context/LNAuthContext';
import { getGenreProfile, updateProfile } from '@shared/services/api';
import { useTheme } from '../context/ThemeContext';
import GenreRadar from '../components/GenreRadar';
import ColorPicker from '../components/ColorPicker';
import LocationSearch from '../components/LocationSearch';
import MobilePage from '../components/MobilePage';
import { waitForImages } from '../logoData.js';

// ── Music DNA View (default) ────────────────────────────────────────────────

function DNAView({ user, onSettings }) {
  const { bgColor } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imagesReady, setImagesReady] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await getGenreProfile();
        setData(result);
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  // Wait for card images to finish loading once the card is mounted
  useEffect(() => {
    if (!data?.has_profile) { setImagesReady(false); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      if (!cardRef.current) return;
      waitForImages(cardRef.current).then(() => { if (!cancelled) setImagesReady(true); });
    }, 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [data]);

  const handleShare = async () => {
    if (!cardRef.current) return;
    try {
      const el = cardRef.current;
      const [{ domToBlob }, { getLogoDataURLs, toDataURL }] = await Promise.all([
        import('modern-screenshot'),
        import('../logoData.js'),
      ]);
      const logos = await getLogoDataURLs();
      // Pre-inline every non-data-URL <img> src (logos + artist images).
      const allImgs = Array.from(el.querySelectorAll('img'));
      const originals = await Promise.all(allImgs.map(async (img) => {
        const orig = img.getAttribute('src') || '';
        if (!orig || orig.startsWith('data:')) return null;
        let next;
        if (orig.includes('logo-icon')) next = logos.icon;
        else if (orig.includes('logo-text')) next = logos.text;
        else next = await toDataURL(orig);
        return { img, orig, next };
      }));
      const swaps = originals.filter(Boolean);
      await Promise.all(swaps.map(({ img, next }) => new Promise((resolve) => {
        const done = () => { img.removeEventListener('load', done); img.removeEventListener('error', done); resolve(); };
        img.addEventListener('load', done);
        img.addEventListener('error', done);
        img.src = next;
      })));
      await Promise.all(swaps.map(({ img }) => img.decode ? img.decode().catch(() => {}) : Promise.resolve()));
      await waitForImages(el);
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
      let blob;
      try {
        blob = await domToBlob(el, { backgroundColor: bgColor, scale: 2 });
      } finally {
        swaps.forEach(({ img, orig }) => { img.src = orig; });
      }
      const filename = `my-music-dna-${Date.now()}.png`;
      const file = new File([blob], filename, { type: 'image/png' });
      const shareText = 'Check out my music taste on Loud Neighbors!';
      const shareUrl = 'https://loudneighbors.com';
      const isNativeApp = window.Capacitor?.isNativePlatform?.();
      if (isNativeApp) {
        try {
          const [{ Filesystem, Directory }, { Share }] = await Promise.all([
            import('@capacitor/filesystem'),
            import('@capacitor/share'),
          ]);
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(String(reader.result).split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          // Cache directory + files[] array makes iOS treat this as image content,
          // unlocking Save Image / Add to Photos / AirDrop image in the share sheet.
          await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Cache });
          const { uri } = await Filesystem.getUri({ path: filename, directory: Directory.Cache });
          await Share.share({
            files: [uri],
            dialogTitle: 'Share your music DNA',
          });
          return;
        } catch (nativeErr) {
          console.warn('Native share failed, falling back:', nativeErr);
        }
      }
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'My Music DNA',
          text: shareText,
          url: shareUrl,
        });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename;
        a.click(); URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error('Share failed:', e);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center py-12 animate-pulse">
        <div className="w-20 h-20 rounded-full bg-white/5 mb-4" />
        <div className="h-4 w-32 bg-white/5 rounded mb-2" />
        <div className="h-3 w-24 bg-white/5 rounded" />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      {/* User header */}
      <div className="flex flex-col items-center mb-6">
        {user?.profile_image_url ? (
          <img src={user.profile_image_url} className="w-20 h-20 rounded-full border-2 border-white/20 mb-2" alt="" />
        ) : (
          <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center text-white font-black text-2xl border-2 border-white/20 mb-2">
            {user?.display_name?.[0]?.toUpperCase() || 'U'}
          </div>
        )}
        <h2 className="text-white font-bold text-lg">{user?.display_name}</h2>
        {user?.username && <p className="text-white/40 text-sm">@{user.username}</p>}
      </div>

      {data?.has_profile ? (
        <>
          {/* Shareable DNA card */}
          <div
            ref={cardRef}
            className="w-full max-w-sm rounded-2xl overflow-hidden p-5 mb-4"
            style={{ backgroundColor: bgColor, border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-white/40 text-xs uppercase tracking-[0.3em]">Your Music DNA</p>
              <img src="/logo-icon.png" alt="LN" className="h-6 opacity-60" />
            </div>

            {/* Top 3 artists */}
            {data.top_artists?.length > 0 && (
              <div className="flex gap-4 justify-center mb-4">
                {data.top_artists.slice(0, 3).map((a, i) => (
                  <div key={a.spotify_id} className="flex flex-col items-center">
                    {a.image_url ? (
                      <img src={a.image_url} alt="" className="w-14 h-14 rounded-full border-2 border-white/20 object-cover" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center text-white font-bold text-lg">
                        {a.name[0]}
                      </div>
                    )}
                    <p className="text-white font-bold text-[10px] mt-1 text-center leading-tight max-w-[60px] truncate">{a.name}</p>
                    <p className="text-white/30 text-[8px]">#{i + 1}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Radar chart */}
            <div className="flex justify-center mb-3">
              <GenreRadar families={data.genre_families} size={200} animate={false} />
            </div>

            {/* Sub-genres */}
            {data.top_subgenres?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 justify-center">
                {data.top_subgenres.slice(0, 5).map(g => (
                  <span key={g.genre} className="px-2 py-0.5 rounded-full bg-white/10 text-white/60 text-[10px] font-medium border border-white/5">
                    {g.genre}
                  </span>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between mt-3">
              <p className="text-white/40 text-[8px] uppercase tracking-[0.25em]">loudneighbors.com</p>
              <img src="/logo-text.png" alt="Loud Neighbors" className="h-6 opacity-40" />
            </div>
          </div>

          {/* Share button */}
          <button
            onClick={handleShare}
            disabled={!imagesReady}
            className="w-full max-w-sm bg-white text-black font-black uppercase tracking-wider text-sm py-3 rounded-full hover:bg-white/90 transition-all mb-3 disabled:opacity-50"
          >
            {imagesReady ? 'Share Your Music DNA' : 'Loading images...'}
          </button>
        </>
      ) : (
        <p className="text-white/30 text-sm mb-6">Listen to more music to see your Music DNA</p>
      )}

      {/* Settings button */}
      <button
        onClick={onSettings}
        className="w-full max-w-sm border border-white/10 text-white/50 font-medium text-sm py-3 rounded-full hover:bg-white/5 transition-all"
      >
        Settings
      </button>
    </div>
  );
}

// ── Settings View ───────────────────────────────────────────────────────────

function SettingsView({ user, setUser, onBack, logout, deleteAccount }) {
  const [locationCity, setLocationCity] = useState(user?.location_city || '');
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await deleteAccount();
      window.location.href = '/';
    } catch (err) {
      console.error('Delete account failed:', err);
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div>
      {/* Back button */}
      <button onClick={onBack} className="flex items-center gap-2 text-white/40 hover:text-white text-sm mb-4">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      <h2 className="text-white font-black text-xl uppercase mb-6">Settings</h2>

      <div className="space-y-8 max-w-xl">
        {/* Location */}
        <div>
          <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Location</h3>
          <LocationSearch
            value={locationCity}
            onChange={async (city, lat, lng) => {
              setLocationCity(city);
              setSaving(true);
              try {
                const result = await updateProfile({ location_city: city, location_lat: lat, location_lng: lng });
                setUser(result.user);
              } catch {}
              setSaving(false);
            }}
            placeholder="Search city..."
          />
          {saving && <p className="text-white/20 text-[10px] mt-1">Saving...</p>}
        </div>

        {/* Appearance */}
        <div>
          <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Appearance</h3>
          <ColorPicker />
        </div>

        {/* Account */}
        <div>
          <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Account</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2">
              <span className="text-white/60 text-sm">Spotify</span>
              <span className="text-white/30 text-sm">Connected</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-white/60 text-sm">Username</span>
              <span className="text-white/30 text-sm">@{user?.username || '—'}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-white/60 text-sm">Member since</span>
              <span className="text-white/30 text-sm">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}
              </span>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div>
          <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Notifications</h3>
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-white/60 text-sm">Email notifications</span>
            <input
              type="checkbox"
              checked={user?.email_notifications ?? true}
              onChange={async (e) => {
                try {
                  const result = await updateProfile({ email_notifications: e.target.checked });
                  setUser(result.user);
                } catch {}
              }}
              className="w-4 h-4 rounded accent-white"
            />
          </label>
        </div>

        {/* Legal */}
        <div>
          <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Legal</h3>
          <div className="space-y-3">
            <a href="/privacy" className="block text-white/40 text-sm hover:text-white/60 transition-colors">Privacy Policy</a>
            <a href="/terms" className="block text-white/40 text-sm hover:text-white/60 transition-colors">Terms of Service</a>
          </div>
        </div>

        <button
          onClick={logout}
          className="w-full bg-white/5 border border-white/10 text-red-400 py-3 rounded-xl font-medium text-sm hover:bg-white/10 transition-colors"
        >
          Log out
        </button>

        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="w-full bg-transparent border border-red-400/40 text-red-400 py-3 rounded-xl font-medium text-sm hover:bg-red-400/10 transition-colors mt-3"
        >
          Delete account
        </button>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => !deleting && setShowDeleteConfirm(false)}>
          <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl max-w-sm w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-bold text-lg mb-2">Delete your account?</h3>
            <p className="text-white/60 text-sm mb-6">
              This signs you out, deletes your profile data, and revokes our access to your Spotify. Aggregated music data may be retained.
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="w-full bg-red-500 text-white py-3 rounded-xl font-bold text-sm hover:bg-red-600 transition-colors disabled:opacity-60"
              >
                {deleting ? 'Deleting…' : 'Yes, delete my account'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="w-full bg-white/5 border border-white/10 text-white py-3 rounded-xl font-medium text-sm hover:bg-white/10 transition-colors disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Profile Page ────────────────────────────────────────────────────────────

export default function ProfilePage() {
  const { user, setUser, logout, deleteAccount } = useLNAuth();
  const [view, setView] = useState('dna'); // 'dna' | 'settings'

  // Reset to DNA when navigating to profile (e.g. pressing profile nav again)
  useEffect(() => {
    const goProfile = () => setView('dna');
    window.addEventListener('ln-go-profile', goProfile);
    return () => window.removeEventListener('ln-go-profile', goProfile);
  }, []);

  return (
    <MobilePage contentClassName="py-4 md:!pt-0">
        {view === 'settings' ? (
          <SettingsView user={user} setUser={setUser} onBack={() => setView('dna')} logout={logout} deleteAccount={deleteAccount} />
        ) : (
          <DNAView user={user} onSettings={() => setView('settings')} />
        )}
    </MobilePage>
  );
}
